// "List payments" dialog for a sale / purchase (legacy helpers/PaymentsComponent.vue).

import { useEffect, useState } from 'react';
import { Alert, Box, Chip, CircularProgress, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';

import { adminApi, apiErrorMessage } from '../../api/client';
import { moneyValue } from '../../crud/configHelpers';
import { formatDateTime, hasTranslation, t } from '../../i18n';
import { gatewayLabel } from '../documents/format';

type PaymentRow = Record<string, unknown> & { id: string };

function name(value: unknown) {
  return value && typeof value === 'object' ? String((value as { name?: unknown }).name ?? '') : '';
}

export function PaymentsListDialog({ endpoint }: { endpoint: string }) {
  const [rows, setRows] = useState<PaymentRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    adminApi
      .get(endpoint)
      .then((response) => active && setRows(Array.isArray(response.data) ? (response.data as PaymentRow[]) : []))
      .catch((err) => active && setError(apiErrorMessage(err, t('common.actionFailed'))));
    return () => {
      active = false;
    };
  }, [endpoint]);

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!rows)
    return (
      <Stack alignItems="center" sx={{ py: 4 }}>
        <CircularProgress size={28} />
      </Stack>
    );
  if (!rows.length) return <Typography color="text.secondary">{t('orderActions.noPayments')}</Typography>;

  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{t('field.createdAt')}</TableCell>
            <TableCell>{t('doc.reference')}</TableCell>
            <TableCell align="right">{t('doc.amount')}</TableCell>
            <TableCell>{t('orderActions.received')}</TableCell>
            <TableCell>{t('doc.gateway')}</TableCell>
            <TableCell>{t('doc.account')}</TableCell>
            <TableCell>{t('orderActions.createdFor')}</TableCell>
            <TableCell align="right">{t('orderActions.paymentAmount')}</TableCell>
            <TableCell>{t('doc.createdBy')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => {
            const pivot = row.pivot as { amount?: unknown } | undefined;
            const received = Number(row.received) === 1;
            return (
              <TableRow key={row.id}>
                <TableCell>{formatDateTime(row.created_at)}</TableCell>
                <TableCell>{String(row.reference ?? '')}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>
                  {moneyValue(pivot?.amount ?? row.amount)}
                </TableCell>
                <TableCell>
                  <Chip size="small" color={received ? 'success' : 'warning'} label={received ? t('status.yes') : t('status.no')} />
                </TableCell>
                <TableCell>{gatewayLabel(row.gateway, t, hasTranslation)}</TableCell>
                <TableCell>{name(row.account)}</TableCell>
                <TableCell>{name(row.payable)}</TableCell>
                <TableCell align="right">{moneyValue(row.amount)}</TableCell>
                <TableCell>{name(row.user)}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
}
